/**
 * P404.1 -- edge bundling for the fan-in.
 *
 * Measured on the demo estate: **80 of 86 service->vendor edges
 * converge on just 5 vendors**, with a maximum fan-in of 24 to one.
 * Only 6 vendors have more than one inbound edge at all. So the
 * remaining visual noise after the spacing pass is not spread evenly --
 * it is five dense sprays of straight lines.
 *
 * Bundling routes the edges sharing a target through a common TRUNK
 * point just short of that target, so they read as one thick relation
 * arriving from a direction rather than twenty-four separate lines
 * crossing everything in between. The information is unchanged; what
 * changes is that the crossings happen inside the bundle instead of
 * across the canvas.
 *
 * Deliberately only for targets ABOVE a fan-in threshold. Bundling a
 * single edge is a curve for nothing -- it adds travel and implies a
 * grouping that does not exist.
 */

export interface Pt {
  x: number;
  y: number;
}

/** Fan-in at which a target's edges start to read as a spray. */
export const MIN_FAN_IN = 4;
/** How far short of the target the trunk sits, as a fraction of the
 *  mean source distance. */
export const TRUNK_PULLBACK = 0.28;

export interface BundleEdge {
  from_id: string;
  to_id: string;
}

/**
 * A trunk point per high-fan-in target: placed between the target and
 * the centroid of everything pointing at it, so the bundle arrives
 * from the direction its sources actually lie in rather than from an
 * arbitrary fixed offset.
 */
export function computeTrunks(
  edges: BundleEdge[],
  positions: Record<string, Pt>,
  opts: { minFanIn?: number; pullback?: number } = {},
): Map<string, Pt> {
  const minFanIn = opts.minFanIn ?? MIN_FAN_IN;
  const pullback = opts.pullback ?? TRUNK_PULLBACK;

  const sources = new Map<string, Pt[]>();
  for (const e of edges) {
    const a = positions[e.from_id];
    const b = positions[e.to_id];
    if (!a || !b) continue;
    const list = sources.get(e.to_id);
    if (list) list.push(a);
    else sources.set(e.to_id, [a]);
  }

  const trunks = new Map<string, Pt>();
  for (const [targetId, pts] of sources) {
    if (pts.length < minFanIn) continue;
    const target = positions[targetId];
    if (!target) continue;
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    trunks.set(targetId, {
      x: target.x + (cx - target.x) * pullback,
      y: target.y + (cy - target.y) * pullback,
    });
  }
  return trunks;
}

/**
 * A path from `a` to `b` funnelled through `trunk`.
 *
 * Both control points sit on the source->trunk line, the second right
 * at the trunk, so every edge sharing a target converges on the same
 * point before the final short run into the node. That shared control
 * is what makes them read as one bundle.
 */
export function bundledPath(a: Pt, b: Pt, trunk: Pt): string {
  const c1x = a.x + (trunk.x - a.x) * 0.62;
  const c1y = a.y + (trunk.y - a.y) * 0.62;
  return `M ${a.x} ${a.y} C ${round(c1x)} ${round(c1y)}, ${round(trunk.x)} ${round(trunk.y)}, ${b.x} ${b.y}`;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
